/**
 * Celestial Racers — Infused Lights arcade engine
 * CR_ENGINE_V3 — clean rebuild: scripted phases, rival lights, butter feel
 * Tagline: Race with Galactic Giants
 * Names locked: Giant Reptilian, Celestial Racers, Race with Galactic Giants, Pixel, Starseed Arcade
 *
 * Sections: Audio | Math/Project | TrackBuilder | Input/Joystick | RaceSim | Renderer | UI
 */
(function (global) {
  'use strict';

  var TEE_URL = 'https://infusedlights.com/products/celestial-racers-t-shirt-1';
  var HUB_URL = '/pages/starseed-arcade';
  var TITLE_BG =
    'https://cdn.shopify.com/s/files/1/3103/2954/files/starseed-arcade-entrance-hero.jpg?v=1789941670';

  var CARS = [
    { id: 'nova', name: 'Nova Drift', speed: 3, grip: 2, boost: 3, color: '#5ee7ff', accent: '#ffffff', body: '#e8f9ff' },
    { id: 'pulse', name: 'Pulse Rider', speed: 2, grip: 4, boost: 2, color: '#ff4ecd', accent: '#ffb8ec', body: '#ffe0f5' },
    { id: 'comet', name: 'Comet Slash', speed: 4, grip: 2, boost: 3, color: '#ffe66d', accent: '#fff4b8', body: '#fff8d6' },
    { id: 'orbit', name: 'Orbit Guard', speed: 2, grip: 3, boost: 4, color: '#a78bfa', accent: '#ddd6fe', body: '#efe9ff' }
  ];

  /* Classic OutRun / Jake Gordon world units */
  var ROAD_W = 2000;
  var SEG_LEN = 200;
  var CAM_H = 1000;
  var CAM_DEPTH = 0.84;
  var DRAW_DIST = 180;
  var RACE_DISTANCE = 150000; /* ~60–90s to 100% with auto-accel */
  var LANES = 3;
  var FOG_DENSITY = 4;
  var PLAYER_Z = SEG_LEN * 6;

  var SCENERY_KINDS = ['pillar', 'crystal', 'arch', 'billboard', 'floatring'];

  var RULES_HTML =
    '<div class="cr-rules" aria-label="How to play">' +
    '  <div class="cr-rules-title">How to play</div>' +
    '  <ol>' +
    '    <li>Stay between the neon rails</li>' +
    '    <li>Grab ★ coins · N boost · shields · chrono</li>' +
    '    <li>Pass rival lights · dodge pink hazards</li>' +
    '    <li>When Giant Reptilian Pressure fills, BOOST</li>' +
    '    <li>Reach 100% — finish the cosmic highway</li>' +
    '  </ol>' +
    '</div>';

  /* ========== Math ========== */
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function rand(a, b) { return a + Math.random() * (b - a); }
  function easeIn(a, b, p) { return a + (b - a) * p * p; }
  function easeOut(a, b, p) { return a + (b - a) * (1 - (1 - p) * (1 - p)); }
  function percent(pos) { return clamp((pos / RACE_DISTANCE) * 100, 0, 100); }
  function phaseName(pct) {
    if (pct < 20) return 'warmup';
    if (pct < 50) return 'cruise';
    if (pct < 80) return 'pressure';
    return 'finale';
  }

  /* ========== Audio ========== */
  function AudioBus() {
    this.ctx = null;
    this.muted = true;
    try {
      var saved = localStorage.getItem('cr-mute');
      if (saved === '0') this.muted = false;
      if (saved === '1') this.muted = true;
    } catch (e) {}
  }
  AudioBus.prototype.ensure = function () {
    if (!this.ctx) {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
    }
    if (this.ctx.state === 'suspended') {
      try { this.ctx.resume(); } catch (e) {}
    }
    return this.ctx;
  };
  AudioBus.prototype.toggle = function () {
    this.muted = !this.muted;
    try { localStorage.setItem('cr-mute', this.muted ? '1' : '0'); } catch (e) {}
    this.ensure();
  };
  AudioBus.prototype.beep = function (freq, dur, type, vol) {
    if (this.muted) return;
    var ctx = this.ensure();
    if (!ctx) return;
    var t0 = ctx.currentTime;
    var o = ctx.createOscillator();
    var g = ctx.createGain();
    o.type = type || 'square';
    o.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(vol || 0.06, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + (dur || 0.08));
    o.connect(g);
    g.connect(ctx.destination);
    o.start(t0);
    o.stop(t0 + (dur || 0.08) + 0.02);
  };
  AudioBus.prototype.arpeggio = function (freqs, step) {
    if (this.muted) return;
    var self = this;
    var s = step || 0.045;
    for (var i = 0; i < freqs.length; i++) {
      (function (f, d) {
        setTimeout(function () { self.beep(f, 0.06, 'triangle', 0.055); }, d * 1000);
      })(freqs[i], i * s);
    }
  };
  AudioBus.prototype.pickup = function () {
    this.arpeggio([660, 880, 1175], 0.04);
  };
  AudioBus.prototype.boostWhoosh = function () {
    if (this.muted) return;
    var ctx = this.ensure();
    if (!ctx) return;
    var t0 = ctx.currentTime;
    var o = ctx.createOscillator();
    var g = ctx.createGain();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(180, t0);
    o.frequency.exponentialRampToValueAtTime(60, t0 + 0.28);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.07, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.3);
    o.connect(g);
    g.connect(ctx.destination);
    o.start(t0);
    o.stop(t0 + 0.32);
  };
  AudioBus.prototype.stompThud = function () {
    this.beep(55, 0.18, 'sine', 0.12);
    this.beep(90, 0.12, 'triangle', 0.06);
  };
  AudioBus.prototype.finishFanfare = function () {
    this.arpeggio([523, 659, 784, 1047, 784, 1047], 0.07);
  };
  AudioBus.prototype.countdownTick = function (n) {
    this.beep(n <= 0 ? 880 : 440, 0.1, 'square', 0.07);
  };
  AudioBus.prototype.hit = function () {
    this.beep(140, 0.14, 'sawtooth', 0.08);
  };
  AudioBus.prototype.passRival = function () {
    this.beep(520, 0.05, 'sine', 0.04);
    this.beep(780, 0.07, 'triangle', 0.045);
  };

  /* ========== Project (Jake Gordon style) ========== */
  function project(p, camX, camY, camZ, camDepth, w, h, roadW) {
    p.camera.x = (p.world.x || 0) - camX;
    p.camera.y = (p.world.y || 0) - camY;
    p.camera.z = (p.world.z || 0) - camZ;
    if (p.camera.z <= camDepth * 0.5) {
      p.screen.scale = 0;
      p.screen.x = 0;
      p.screen.y = 0;
      p.screen.w = 0;
      return;
    }
    var scale = camDepth / p.camera.z;
    p.screen.scale = scale;
    p.screen.x = Math.round(w / 2 + (scale * p.camera.x * w) / 2);
    p.screen.y = Math.round(h / 2 - (scale * p.camera.y * h) / 2);
    p.screen.w = Math.round((scale * roadW * w) / 2);
  }

  function rumbleWidth(projectedRoadWidth, lanes) {
    return projectedRoadWidth / Math.max(6, 2 * lanes);
  }
  function laneMarkerWidth(projectedRoadWidth, lanes) {
    return projectedRoadWidth / Math.max(32, 8 * lanes);
  }
  function railWidth(projectedRoadWidth) {
    return Math.max(3, projectedRoadWidth * 0.035);
  }
  function poly(ctx, x1, y1, x2, y2, x3, y3, x4, y4, color) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x3, y3);
    ctx.lineTo(x4, y4);
    ctx.closePath();
    ctx.fill();
  }

  /* ========== TrackBuilder ========== */
  function TrackBuilder() {}
  TrackBuilder.build = function () {
    var segs = [];
    var y = 0;

    function addSeg(c, yDest) {
      var i = segs.length;
      segs.push({
        index: i,
        p1: { world: { x: 0, y: y, z: i * SEG_LEN }, camera: {}, screen: {} },
        p2: { world: { x: 0, y: yDest, z: (i + 1) * SEG_LEN }, camera: {}, screen: {} },
        curve: c,
        sprites: [],
        color: i % 2
      });
      y = yDest;
    }
    function addRoad(enter, hold, leave, curveIn, yDelta) {
      var startY = y;
      var endY = startY + yDelta;
      var n, total, p;
      total = enter + hold + leave;
      if (total < 1) return;
      for (n = 0; n < enter; n++) {
        p = n / Math.max(1, enter);
        addSeg(easeIn(0, curveIn, p), lerp(startY, endY, p * p));
      }
      for (n = 0; n < hold; n++) addSeg(curveIn, endY);
      for (n = 0; n < leave; n++) {
        p = (n + 1) / leave;
        addSeg(easeIn(curveIn, 0, n / leave), lerp(endY, startY, p * p));
      }
    }

    /* Scripted road shape — not random soup */
    /* Warmup: mostly straight, gentle intro */
    addRoad(30, 80, 30, 0, 0);
    addRoad(20, 40, 20, 2, 10);
    addRoad(25, 50, 25, -2.5, 0);
    addRoad(20, 60, 20, 0, -15);
    /* Cruise: flowing S-curves */
    addRoad(30, 70, 30, 4, 20);
    addRoad(25, 55, 25, -5, -10);
    addRoad(20, 40, 20, 3, 30);
    addRoad(30, 60, 30, -3.5, 0);
    addRoad(15, 35, 15, 0, -25);
    addRoad(28, 50, 28, 5.5, 15);
    /* Pressure: tighter bends + hills */
    addRoad(22, 45, 22, -6, 40);
    addRoad(18, 40, 18, 7, -20);
    addRoad(25, 50, 25, -4, 10);
    addRoad(20, 35, 20, 6.5, -30);
    addRoad(30, 55, 30, -5, 25);
    /* Finale: spectacle then clear finish stretch */
    addRoad(20, 40, 20, 8, 35);
    addRoad(25, 45, 25, -7, -15);
    addRoad(15, 30, 15, 4, 0);
    addRoad(40, 100, 40, 0, 0); /* glory straight */

    while (segs.length * SEG_LEN < RACE_DISTANCE + 8000) {
      addRoad(20, 40, 20, rand(-3, 3), rand(-20, 20));
    }
    addRoad(50, 120, 50, 0, 0);

    return { segments: segs, trackLength: segs.length * SEG_LEN };
  };

  TrackBuilder.buildEntities = function () {
    var ents = [];
    var d, i;

    /* ---- Phase-scripted pickups & hazards ---- */
    /* Warmup 0–20%: 2–3 easy stars, almost no hazards, tip zone */
    var warmupZs = [3500, 7000, 11000];
    for (i = 0; i < warmupZs.length; i++) {
      ents.push({ z: warmupZs[i], x: (i % 2 === 0 ? -0.4 : 0.45), kind: 'coin', alive: true, pulse: 0, tip: i === 0 });
    }
    ents.push({ z: 9000, x: 0.2, kind: 'boost', alive: true, pulse: 0 });

    /* Cruise 20–50%: spaced goodies, rare crack */
    for (d = 32000; d < 75000; d += rand(2800, 4200)) {
      var roll = Math.random();
      var kind = roll < 0.45 ? 'coin' : roll < 0.7 ? 'boost' : roll < 0.88 ? 'shield' : 'chrono';
      ents.push({ z: d, x: rand(-0.7, 0.7), kind: kind, alive: true, pulse: Math.random() * 6 });
    }
    for (d = 40000; d < 72000; d += rand(9000, 14000)) {
      ents.push({ z: d, x: rand(-0.55, 0.55), kind: 'crack', alive: true, pulse: 0, telegraph: 1 });
    }
    for (d = 38000; d < 70000; d += rand(11000, 16000)) {
      if (Math.random() < 0.4) {
        ents.push({ z: d, x: rand(-0.6, 0.6), kind: 'debris', alive: true, pulse: 0, telegraph: 1 });
      }
    }

    /* Pressure 50–80%: more boost pads, 1–2 stomps, cracks */
    for (d = 76000; d < 118000; d += rand(1800, 2800)) {
      var r2 = Math.random();
      var k2 = r2 < 0.35 ? 'coin' : r2 < 0.62 ? 'boost' : r2 < 0.82 ? 'shield' : 'chrono';
      ents.push({ z: d, x: rand(-0.75, 0.75), kind: k2, alive: true, pulse: Math.random() * 6 });
    }
    for (d = 80000; d < 115000; d += rand(5500, 8500)) {
      ents.push({ z: d, x: rand(-0.5, 0.5), kind: 'crack', alive: true, pulse: 0, telegraph: 1 });
    }
    var stomps = [88000, 105000];
    for (i = 0; i < stomps.length; i++) {
      ents.push({ z: stomps[i], x: 0, kind: 'stomp', alive: true, armed: true, pulse: 0, telegraph: 1 });
    }
    for (d = 82000; d < 112000; d += rand(7000, 10000)) {
      ents.push({ z: d, x: rand(-0.65, 0.65), kind: 'debris', alive: true, pulse: 0, telegraph: 1 });
    }

    /* Finale 80–100%: spectacle pickups then clear stretch */
    for (d = 122000; d < 138000; d += rand(1600, 2400)) {
      ents.push({
        z: d,
        x: rand(-0.6, 0.6),
        kind: Math.random() < 0.5 ? 'coin' : 'boost',
        alive: true,
        pulse: Math.random() * 6
      });
    }
    /* clear finish glory — almost nothing after 140k */

    /* Roadside scenery — spaced, not cluttered */
    var side = 1;
    for (var scz = 600; scz < RACE_DISTANCE + 2000; scz += rand(700, 1100)) {
      var sk = SCENERY_KINDS[Math.floor(Math.random() * SCENERY_KINDS.length)];
      var ox = side * (1.4 + Math.random() * 0.4);
      ents.push({ z: scz, x: ox, kind: sk, scenery: true, alive: true, pulse: Math.random() * 6 });
      if (Math.random() < 0.18) {
        ents.push({
          z: scz + rand(50, 140),
          x: -ox * (0.9 + Math.random() * 0.15),
          kind: SCENERY_KINDS[Math.floor(Math.random() * SCENERY_KINDS.length)],
          scenery: true,
          alive: true,
          pulse: Math.random() * 6
        });
      }
      side *= -1;
    }

    return ents;
  };

  TrackBuilder.buildRivals = function () {
    /* 5 AI rival lights — ahead/behind lanes, soft bump only */
    var rivals = [];
    var colors = ['#7af0ff', '#ff8ad8', '#ffe88a', '#b8a0ff', '#8affc8', '#ffaa77'];
    var starts = [4500, 12000, 22000, 38000, 55000, 72000];
    var n = 5;
    for (var i = 0; i < n; i++) {
      rivals.push({
        id: i,
        z: starts[i] || (8000 + i * 14000),
        x: (i % 3) - 1,
        lane: (i % 3) - 1,
        speed: 2400 + (i % 3) * 180 + Math.random() * 200,
        color: colors[i % colors.length],
        alive: true,
        passed: false,
        bob: Math.random() * 6,
        softHitCd: 0
      });
    }
    return rivals;
  };

  /* ========== Game ========== */
  function CelestialRacers(opts) {
    opts = opts || {};
    this.root = typeof opts.root === 'string' ? document.querySelector(opts.root) : opts.root;
    if (!this.root) throw new Error('CelestialRacers: root element required');
    this.teeUrl = opts.teeUrl || TEE_URL;
    this.hubUrl = opts.hubUrl || HUB_URL;
    this.audio = new AudioBus();
    this.state = 'title';
    this.carIndex = 0;
    this.keys = {};
    this.touch = {
      left: false, right: false, accel: false, brake: false, boost: false,
      steer: 0, steerSmooth: 0, joyActive: false
    };
    this.raf = 0;
    this.lastTs = 0;
    this.stars = [];
    this.W = 960;
    this.H = 600;
    this._titleImg = null;
    this._tipText = '';
    this._tipTimer = 0;
    this._loadTitleArt();
    this._buildDom();
    this._bind();
    this._initStars();
    this._resize();
    this.show('title');
    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);
  }

  CelestialRacers.prototype._loadTitleArt = function () {
    var self = this;
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () { self._titleImg = img; };
    img.src = TITLE_BG;
  };

  CelestialRacers.prototype._buildDom = function () {
    var r = this.root;
    r.classList.add('cr-root');
    r.innerHTML =
      '<div class="cr-header">' +
      '  <p class="cr-brand">Infused Lights · Starseed Arcade<strong>Celestial Racers</strong></p>' +
      '  <div class="cr-toolbar">' +
      '    <button type="button" class="sa-btn sa-btn-ghost" data-act="mute" aria-label="Toggle sound">Sound Off</button>' +
      '    <a class="sa-btn sa-btn-ghost" href="' + this.hubUrl + '">Arcade Hub</a>' +
      '  </div>' +
      '</div>' +
      '<div class="cr-stage" id="cr-stage">' +
      '  <canvas id="cr-canvas" width="960" height="600" aria-label="Celestial Racers race track"></canvas>' +
      '  <div class="cr-hud" id="cr-hud" hidden>' +
      '    <div class="top">' +
      '      <span class="chip" id="cr-score">SCORE 0</span>' +
      '      <span class="chip gold" id="cr-dist">0%</span>' +
      '      <span class="chip" id="cr-speed">0 km/h</span>' +
      '      <span class="chip gold" id="cr-coins">★ 0</span>' +
      '      <span class="chip pink" id="cr-status">WARMUP</span>' +
      '    </div>' +
      '    <div class="cr-boost-meter" title="Boost"><div class="fill" id="cr-boost-fill"></div></div>' +
      '    <div class="cr-pressure-label">Giant Reptilian Pressure</div>' +
      '    <div class="cr-pressure"><div class="fill" id="cr-pressure-fill"></div></div>' +
      '    <div class="cr-phase-tip" id="cr-phase-tip" hidden></div>' +
      '  </div>' +
      '  <div class="cr-fun" id="cr-fun">+FUN</div>' +
      '  <div class="cr-overlay cr-overlay-title" id="cr-overlay-title">' +
      '    <p class="tagline">Race with Galactic Giants</p>' +
      '    <h1>Celestial Racers</h1>' +
      '    <p class="cr-onboard">Neon rails · Rival lights · Giant Reptilian pressure · Hit 100%</p>' +
      RULES_HTML +
      '    <div class="cr-actions">' +
      '      <button type="button" class="sa-btn sa-btn-play" data-act="to-select">START</button>' +
      '    </div>' +
      '    <p class="cr-hint">Joystick L · BOOST R · Auto-accel · Arrows / WASD · Space boost</p>' +
      '  </div>' +
      '  <div class="cr-overlay hidden" id="cr-overlay-select">' +
      '    <h2>Choose Your Racer</h2>' +
      '    <p>Speed · Grip · Boost — pick your vibe.</p>' +
      RULES_HTML +
      '    <div class="cr-cars" id="cr-cars"></div>' +
      '    <div class="cr-actions">' +
      '      <button type="button" class="sa-btn sa-btn-play" data-act="to-race">RACE</button>' +
      '      <button type="button" class="sa-btn sa-btn-ghost" data-act="to-title">Back</button>' +
      '    </div>' +
      '  </div>' +
      '  <div class="cr-overlay hidden" id="cr-overlay-results">' +
      '    <h2 id="cr-result-title">FINISH!</h2>' +
      '    <p id="cr-result-body"></p>' +
      '    <div class="cr-actions">' +
      '      <button type="button" class="sa-btn sa-btn-play" data-act="retry">Race Again</button>' +
      '      <a class="sa-btn sa-btn-pink" href="' + this.teeUrl + '" target="_blank" rel="noopener">Grab the Tee</a>' +
      '      <a class="sa-btn sa-btn-ghost" href="' + this.hubUrl + '">Arcade Hub</a>' +
      '    </div>' +
      '  </div>' +
      '  <div class="cr-touch" id="cr-touch" hidden>' +
      '    <div class="cr-joy" id="cr-joy" aria-label="Steer">' +
      '      <div class="cr-joy-base"></div>' +
      '      <div class="cr-joy-knob" id="cr-joy-knob"></div>' +
      '    </div>' +
      '    <button type="button" data-touch="boost" class="cr-boost-btn" aria-label="Boost">BOOST</button>' +
      '  </div>' +
      '</div>';

    this.canvas = r.querySelector('#cr-canvas');
    this.ctx = this.canvas.getContext('2d');
    this.stage = r.querySelector('#cr-stage');
    this.hud = r.querySelector('#cr-hud');
    this.funEl = r.querySelector('#cr-fun');
    this.phaseTipEl = r.querySelector('#cr-phase-tip');
    this.overlays = {
      title: r.querySelector('#cr-overlay-title'),
      select: r.querySelector('#cr-overlay-select'),
      results: r.querySelector('#cr-overlay-results')
    };
    this.touchBar = r.querySelector('#cr-touch');
    this.muteBtn = r.querySelector('[data-act="mute"]');
    this._renderCarCards();
    this._syncMuteLabel();
  };

  CelestialRacers.prototype._statBars = function (n) {
    var s = '';
    for (var i = 1; i <= 4; i++) s += '<span class="' + (i <= n ? 'on' : '') + '"></span>';
    return s;
  };

  CelestialRacers.prototype._renderCarCards = function () {
    var wrap = this.root.querySelector('#cr-cars');
    var html = '';
    for (var i = 0; i < CARS.length; i++) {
      var c = CARS[i];
      html +=
        '<button type="button" class="cr-car' + (i === this.carIndex ? ' selected' : '') +
        '" data-car="' + i + '" data-car-id="' + c.id + '">' +
        '<div class="cr-car-swatch" style="--cr-car:' + c.color + ';--cr-car-acc:' + c.accent +
        ';--cr-car-body:' + (c.body || c.accent) + '" aria-hidden="true"></div>' +
        '<div class="name" style="color:' + c.color + '">' + c.name + '</div>' +
        '<div class="stat">SPD <span class="bars">' + this._statBars(c.speed) + '</span></div>' +
        '<div class="stat">GRP <span class="bars">' + this._statBars(c.grip) + '</span></div>' +
        '<div class="stat">BST <span class="bars">' + this._statBars(c.boost) + '</span></div>' +
        '</button>';
    }
    wrap.innerHTML = html;
  };

  /* ========== Input / Joystick ========== */
  CelestialRacers.prototype._bind = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var t = e.target.closest('[data-act]');
      if (t) {
        var act = t.getAttribute('data-act');
        if (act === 'mute') {
          self.audio.toggle();
          self._syncMuteLabel();
          if (!self.audio.muted) self.audio.beep(660, 0.06, 'sine', 0.04);
        } else if (act === 'to-select') self.show('select');
        else if (act === 'to-title') self.show('title');
        else if (act === 'to-race') self.startRace();
        else if (act === 'retry') self.show('select');
      }
      var car = e.target.closest('[data-car]');
      if (car) {
        self.carIndex = parseInt(car.getAttribute('data-car'), 10);
        self._renderCarCards();
        self.audio.beep(520 + self.carIndex * 80, 0.05, 'square', 0.03);
      }
    });

    function setKey(e, down) {
      var k = e.key.toLowerCase();
      self.keys[k] = down;
      if (['arrowleft', 'arrowright', 'arrowup', 'arrowdown', ' ', 'a', 'd', 'w', 's'].indexOf(k) >= 0) {
        e.preventDefault();
      }
    }
    window.addEventListener('keydown', function (e) {
      setKey(e, true);
      if (e.key === 'Enter' && self.state === 'title') self.show('select');
      else if (e.key === 'Enter' && self.state === 'select') self.startRace();
      else if (e.key === 'm' || e.key === 'M') {
        self.audio.toggle();
        self._syncMuteLabel();
      }
    });
    window.addEventListener('keyup', function (e) { setKey(e, false); });

    function bindTouch(btn) {
      var key = btn.getAttribute('data-touch');
      function on(v) {
        return function (ev) {
          ev.preventDefault();
          self.touch[key] = v;
          btn.classList.toggle('active', v);
        };
      }
      btn.addEventListener('pointerdown', on(true));
      btn.addEventListener('pointerup', on(false));
      btn.addEventListener('pointercancel', on(false));
      btn.addEventListener('pointerleave', on(false));
      btn.addEventListener('touchstart', on(true), { passive: false });
      btn.addEventListener('touchend', on(false), { passive: false });
      btn.addEventListener('touchcancel', on(false), { passive: false });
    }
    var touchBtns = this.root.querySelectorAll('[data-touch]');
    for (var i = 0; i < touchBtns.length; i++) bindTouch(touchBtns[i]);

    self._bindJoystick();
    window.addEventListener('resize', function () { self._resize(); });
  };

  CelestialRacers.prototype._bindJoystick = function () {
    var self = this;
    var joy = this.root.querySelector('#cr-joy');
    var knob = this.root.querySelector('#cr-joy-knob');
    if (!joy || !knob) return;
    var radius = 48;
    var deadzone = 0.12;
    var activeId = null;

    function setKnob(nx, ny) {
      knob.style.transform = 'translate(' + Math.round(nx * radius) + 'px,' + Math.round(ny * radius) + 'px)';
    }
    function resetJoy() {
      activeId = null;
      self.touch.steer = 0;
      self.touch.joyActive = false;
      self.touch.left = false;
      self.touch.right = false;
      self.touch.brake = false;
      setKnob(0, 0);
      joy.classList.remove('active');
    }
    function applyFromEvent(ev) {
      var rect = joy.getBoundingClientRect();
      var cx = rect.left + rect.width / 2;
      var cy = rect.top + rect.height / 2;
      var clientX = ev.clientX;
      var clientY = ev.clientY;
      if (ev.touches && ev.touches[0]) {
        clientX = ev.touches[0].clientX;
        clientY = ev.touches[0].clientY;
      }
      var dx = (clientX - cx) / radius;
      var dy = (clientY - cy) / radius;
      var mag = Math.sqrt(dx * dx + dy * dy);
      if (mag > 1) { dx /= mag; dy /= mag; mag = 1; }
      setKnob(dx, dy);
      /* deadzone */
      var steer = Math.abs(dx) < deadzone ? 0 : dx;
      /* soften near center */
      if (Math.abs(steer) > 0) {
        var sign = steer < 0 ? -1 : 1;
        steer = sign * ((Math.abs(steer) - deadzone) / (1 - deadzone));
      }
      self.touch.steer = clamp(steer, -1, 1);
      self.touch.left = self.touch.steer < -0.08;
      self.touch.right = self.touch.steer > 0.08;
      self.touch.brake = dy > 0.6;
      self.touch.joyActive = true;
    }

    function onDown(ev) {
      ev.preventDefault();
      activeId = ev.pointerId != null ? ev.pointerId : 'touch';
      joy.classList.add('active');
      try { joy.setPointerCapture && joy.setPointerCapture(ev.pointerId); } catch (e) {}
      applyFromEvent(ev);
      self.audio.ensure();
    }
    function onMove(ev) {
      if (activeId === null) return;
      if (ev.pointerId != null && ev.pointerId !== activeId) return;
      ev.preventDefault();
      applyFromEvent(ev);
    }
    function onUp(ev) {
      if (activeId === null) return;
      if (ev.pointerId != null && ev.pointerId !== activeId) return;
      ev.preventDefault();
      resetJoy();
    }

    joy.addEventListener('pointerdown', onDown);
    joy.addEventListener('pointermove', onMove);
    joy.addEventListener('pointerup', onUp);
    joy.addEventListener('pointercancel', onUp);
    joy.addEventListener('touchstart', onDown, { passive: false });
    joy.addEventListener('touchmove', onMove, { passive: false });
    joy.addEventListener('touchend', onUp, { passive: false });
    joy.addEventListener('touchcancel', onUp, { passive: false });
  };

  CelestialRacers.prototype._syncMuteLabel = function () {
    if (this.muteBtn) this.muteBtn.textContent = this.audio.muted ? 'Sound Off' : 'Sound On';
  };

  CelestialRacers.prototype._initStars = function () {
    this.stars = [];
    for (var i = 0; i < 90; i++) {
      this.stars.push({
        x: Math.random(),
        y: Math.random() * 0.55,
        s: 0.4 + Math.random() * 1.6,
        tw: Math.random() * Math.PI * 2
      });
    }
  };

  CelestialRacers.prototype._resize = function () {
    var stage = this.stage;
    if (!stage) return;
    var rect = stage.getBoundingClientRect();
    var w = Math.max(320, Math.floor(rect.width));
    var h = Math.max(240, Math.floor(rect.height));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.W = w;
    this.H = h;
  };

  CelestialRacers.prototype.show = function (name) {
    this.state = name;
    var o = this.overlays;
    o.title.classList.toggle('hidden', name !== 'title');
    o.select.classList.toggle('hidden', name !== 'select');
    o.results.classList.toggle('hidden', name !== 'results');
    var racing = name === 'race';
    this.hud.hidden = !racing;
    this.touchBar.hidden = !racing;
    if (name === 'select') this._renderCarCards();
  };

  /* ========== RaceSim ========== */
  CelestialRacers.prototype.startRace = function () {
    var car = CARS[this.carIndex];
    var track = TrackBuilder.build();
    this.race = {
      car: car,
      position: 0,
      playerX: 0,
      playerXSmooth: 0,
      speed: 0,
      maxSpeed: 3600 + car.speed * 650,
      accel: 2000 + car.speed * 240,
      brake: 4000,
      centrifugal: 0.00016 - car.grip * 0.000032,
      steerRate: 1.55 + car.grip * 0.12,
      boostPower: 1.28 + car.boost * 0.08,
      boostFuel: 0.62,
      score: 0,
      coins: 0,
      shield: 0,
      chrono: 0,
      pressure: 0,
      hitFlash: 0,
      roarPulse: 0,
      shake: 0,
      finished: false,
      time: 0,
      funTimer: 0,
      funLabel: '+FUN',
      segments: track.segments,
      trackLength: track.trackLength,
      entities: TrackBuilder.buildEntities(),
      rivals: TrackBuilder.buildRivals(),
      curve: 0,
      skyOffset: 0,
      boosting: false,
      countdown: 3.25,
      countdownBeep: 4,
      iFrames: 0,
      particles: [],
      speedLines: [],
      camBob: 0,
      finishFanfare: 0,
      speedNorm: 0,
      fovPunch: 0,
      phaseShown: '',
      tipShown: false,
      magnetR: 0.28,
      passes: 0
    };
    this.show('race');
    this._setTip('Warmup — grab ★ · feel the rails', 4.5);
    this.audio.beep(330, 0.12, 'square', 0.05);
  };

  CelestialRacers.prototype._setTip = function (text, dur) {
    this._tipText = text;
    this._tipTimer = dur || 3;
    if (this.phaseTipEl) {
      this.phaseTipEl.textContent = text;
      this.phaseTipEl.hidden = false;
    }
  };

  CelestialRacers.prototype.loop = function (ts) {
    var dt = Math.min(0.05, (ts - (this.lastTs || ts)) / 1000);
    this.lastTs = ts;
    if (this.state === 'race' && this.race && !this.race.finished) {
      this._updateRace(dt);
    }
    this._draw(dt);
    this.raf = requestAnimationFrame(this.loop);
  };

  CelestialRacers.prototype._input = function () {
    var joySteer = this.touch.steerSmooth != null ? this.touch.steerSmooth : this.touch.steer;
    /* smooth analog steer from joystick */
    var targetSteer = this.touch.steer || 0;
    this.touch.steerSmooth = lerp(this.touch.steerSmooth || 0, targetSteer, 0.28);

    var keyL = this.keys['arrowleft'] || this.keys['a'];
    var keyR = this.keys['arrowright'] || this.keys['d'];
    var steer = this.touch.steerSmooth;
    if (keyL) steer = Math.min(steer, -1);
    if (keyR) steer = Math.max(steer, 1);
    if (keyL && !keyR) steer = -1;
    if (keyR && !keyL) steer = 1;
    if (keyL && keyR) steer = 0;

    var accel = true; /* auto-accel */
    var brake = this.keys['arrowdown'] || this.keys['s'] || this.touch.brake;
    var boost = this.keys[' '] || this.touch.boost;
    return { steer: clamp(steer, -1, 1), accel: accel, brake: brake, boost: boost };
  };

  CelestialRacers.prototype._updateRace = function (dt) {
    var r = this.race;
    if (!r) return;

    /* Countdown 3-2-1-GO */
    if (r.countdown > 0) {
      var prev = Math.ceil(r.countdown);
      r.countdown -= dt;
      var now = Math.ceil(Math.max(0, r.countdown));
      if (now < r.countdownBeep) {
        r.countdownBeep = now;
        this.audio.countdownTick(now);
      }
      this._updateHud();
      return;
    }

    var inp = this._input();
    var car = r.car;
    var pct = percent(r.position);
    var ph = phaseName(pct);

    /* Phase tips once */
    if (ph !== r.phaseShown) {
      r.phaseShown = ph;
      if (ph === 'cruise') this._setTip('Cruise — pass rival lights · collect', 3.2);
      else if (ph === 'pressure') this._setTip('Pressure — BOOST vs Giant Reptilian', 3.5);
      else if (ph === 'finale') this._setTip('Finale — clear stretch to glory!', 3.2);
    }
    if (this._tipTimer > 0) {
      this._tipTimer -= dt;
      if (this._tipTimer <= 0 && this.phaseTipEl) this.phaseTipEl.hidden = true;
    }

    /* Speed */
    var maxSp = r.maxSpeed * (r.chrono > 0 ? 0.82 : 1);
    if (inp.brake) {
      r.speed = Math.max(0, r.speed - r.brake * dt);
    } else if (inp.accel) {
      r.speed += r.accel * dt;
    }
    if (r.speed > maxSp) r.speed = lerp(r.speed, maxSp, 0.08);

    /* Boost */
    var boostActive = false;
    if (inp.boost && r.boostFuel > 0.02 && r.speed > 400) {
      boostActive = true;
      r.boosting = true;
      r.boostFuel = Math.max(0, r.boostFuel - dt * 0.38);
      r.speed = Math.min(r.speed * 1.002 + 900 * dt, maxSp * r.boostPower);
      r.fovPunch = Math.min(1, r.fovPunch + dt * 3);
      r.score += Math.floor(18 * dt * 60);
      if (!r._boostSfx || r._boostSfx <= 0) {
        this.audio.boostWhoosh();
        r._boostSfx = 0.45;
      }
    } else {
      r.boosting = false;
      r.fovPunch = Math.max(0, r.fovPunch - dt * 2.2);
      if (r.boostFuel < 1) r.boostFuel = Math.min(1, r.boostFuel + dt * 0.09);
    }
    if (r._boostSfx > 0) r._boostSfx -= dt;

    /* Segment curve under player */
    var playerSeg = Math.floor(r.position / SEG_LEN) % r.segments.length;
    var seg = r.segments[playerSeg] || r.segments[0];
    var curve = seg.curve || 0;
    r.curve = lerp(r.curve, curve, 0.08);

    /* Steering + centrifugal — butter lerp */
    var dx = inp.steer * r.steerRate * (r.speed / Math.max(1, r.maxSpeed)) * dt * 2.1;
    /* soft lane snap when nearly centered and not steering hard */
    if (Math.abs(inp.steer) < 0.18 && Math.abs(r.playerX) < 0.22 && r.speed > 800) {
      dx += -r.playerX * 1.8 * dt;
    }
    r.playerX += dx;
    /* centrifugal pull on curves */
    r.playerX -= r.curve * r.centrifugal * r.speed * dt * 55;

    /* Gentle rail bounce (not harsh wall) */
    var rail = 1.05;
    if (r.playerX < -rail) {
      r.playerX = -rail + ( -rail - r.playerX) * -0.35;
      r.playerX = Math.max(r.playerX, -rail - 0.08);
      r.speed *= 0.985;
    } else if (r.playerX > rail) {
      r.playerX = rail - (r.playerX - rail) * -0.35;
      r.playerX = Math.min(r.playerX, rail + 0.08);
      r.speed *= 0.985;
    }
    r.playerX = clamp(r.playerX, -1.18, 1.18);
    r.playerXSmooth = lerp(r.playerXSmooth, r.playerX, 0.22);

    /* Advance */
    var move = r.speed * dt;
    r.position += move;
    r.skyOffset += r.curve * r.speed * dt * 0.00015;
    r.time += dt;
    r.speedNorm = r.speed / Math.max(1, r.maxSpeed);
    r.camBob = Math.sin(r.time * 10) * r.speedNorm * 1.2;

    /* Pressure meter — phase aware */
    var pRate = 0;
    if (ph === 'warmup') pRate = 0.01;
    else if (ph === 'cruise') pRate = 0.045;
    else if (ph === 'pressure') pRate = 0.11;
    else pRate = 0.07;
    if (boostActive) pRate *= 0.25;
    if (r.shield > 0) pRate *= 0.4;
    r.pressure = clamp(r.pressure + pRate * dt - (boostActive ? 0.35 * dt : 0), 0, 1);
    if (r.pressure > 0.92) {
      r.roarPulse = 1;
      if (r.pressure >= 1 && r.iFrames <= 0) {
        this._applyHit(0.35, 'PRESSURE!');
        r.pressure = 0.55;
      }
    } else {
      r.roarPulse = Math.max(0, r.roarPulse - dt);
    }

    if (r.shield > 0) r.shield -= dt;
    if (r.chrono > 0) r.chrono -= dt;
    if (r.iFrames > 0) r.iFrames -= dt;
    if (r.hitFlash > 0) r.hitFlash -= dt;
    if (r.shake > 0) r.shake -= dt;
    if (r.funTimer > 0) r.funTimer -= dt;

    /* Entities */
    this._updateEntities(dt);
    this._updateRivals(dt);
    this._updateJuice(dt, boostActive);

    /* Win */
    if (r.position >= RACE_DISTANCE && !r.finished) {
      this._finish(true);
      return;
    }

    this._updateHud();
  };

  CelestialRacers.prototype._updateEntities = function (dt) {
    var r = this.race;
    var magnet = r.magnetR + (r.boosting ? 0.08 : 0);
    for (var i = 0; i < r.entities.length; i++) {
      var e = r.entities[i];
      if (!e.alive || e.scenery) continue;
      e.pulse = (e.pulse || 0) + dt * 4;
      var dz = e.z - r.position;
      /* Telegraph pink pulse when near */
      if (e.telegraph && dz > 0 && dz < 2800) e.warn = 1 - dz / 2800;
      else e.warn = 0;

      if (dz < -200 || dz > DRAW_DIST * SEG_LEN) continue;
      if (dz > 40 && dz < 420) {
        var dx = Math.abs(e.x - r.playerXSmooth);
        var hitR = 0.22;
        if (e.kind === 'coin' || e.kind === 'boost' || e.kind === 'shield' || e.kind === 'chrono') {
          /* generous magnet when close */
          if (dx < magnet + 0.12 && dz < 380) {
            e.x = lerp(e.x, r.playerXSmooth, 0.15);
            dx = Math.abs(e.x - r.playerXSmooth);
          }
          hitR = 0.32;
        } else if (e.kind === 'crack') hitR = 0.16;
        else if (e.kind === 'debris') hitR = 0.18;
        else if (e.kind === 'stomp') hitR = 0.38;

        if (dx < hitR && dz < 320) {
          this._collect(e);
        }
      }
    }
  };

  CelestialRacers.prototype._updateRivals = function (dt) {
    var r = this.race;
    for (var i = 0; i < r.rivals.length; i++) {
      var v = r.rivals[i];
      if (!v.alive) continue;
      v.bob += dt * 3;
      v.z += v.speed * dt;
      /* gentle lane drift */
      v.x = lerp(v.x, v.lane * 0.55, 0.02);
      if (Math.random() < 0.002) v.lane = Math.floor(Math.random() * 3) - 1;
      if (v.softHitCd > 0) v.softHitCd -= dt;

      var dz = v.z - r.position;
      /* Pass for score */
      if (!v.passed && dz < -80 && dz > -600) {
        v.passed = true;
        r.passes++;
        r.score += 250;
        this._fun('+PASS');
        this.audio.passRival();
      }
      /* Soft bump — not death */
      if (dz > 40 && dz < 280 && Math.abs(v.x - r.playerXSmooth) < 0.28 && v.softHitCd <= 0) {
        v.softHitCd = 0.8;
        r.speed *= 0.88;
        r.playerX += (r.playerXSmooth > v.x ? 0.12 : -0.12);
        r.shake = 0.15;
        r.score = Math.max(0, r.score - 40);
        this._fun('BUMP');
        this.audio.hit();
      }
      /* Recycle rivals ahead if far behind */
      if (dz < -4000) {
        v.z = r.position + rand(6000, 14000);
        v.passed = false;
        v.lane = Math.floor(Math.random() * 3) - 1;
        v.x = v.lane * 0.55;
      }
    }
  };

  CelestialRacers.prototype._collect = function (e) {
    var r = this.race;
    if (!e.alive) return;
    e.alive = false;
    if (e.kind === 'coin') {
      r.coins++;
      r.score += 100;
      this._fun('+★');
      this.audio.pickup();
      this._spawnBurst(e.x, '#ffe66d');
    } else if (e.kind === 'boost') {
      r.boostFuel = Math.min(1, r.boostFuel + 0.42);
      r.score += 80;
      this._fun('N BOOST');
      this.audio.arpeggio([440, 660, 880], 0.035);
      this._spawnBurst(e.x, '#5ee7ff');
    } else if (e.kind === 'shield') {
      r.shield = Math.max(r.shield, 4.5);
      r.score += 120;
      this._fun('SHIELD');
      this.audio.arpeggio([520, 780], 0.05);
      this._spawnBurst(e.x, '#a78bfa');
    } else if (e.kind === 'chrono') {
      r.chrono = Math.max(r.chrono, 3.2);
      r.score += 90;
      this._fun('CHRONO');
      this.audio.arpeggio([392, 523, 659], 0.04);
      this._spawnBurst(e.x, '#7af0c8');
    } else if (e.kind === 'crack' || e.kind === 'debris') {
      if (r.iFrames > 0) { e.alive = true; return; }
      if (r.shield > 0) {
        r.shield = 0;
        r.iFrames = 1.1;
        this._fun('BLOCKED');
        this.audio.beep(300, 0.08, 'triangle', 0.05);
      } else {
        this._applyHit(0.28, 'OUCH');
      }
    } else if (e.kind === 'stomp') {
      if (r.iFrames > 0) { e.alive = true; return; }
      this.audio.stompThud();
      r.roarPulse = 1;
      r.shake = 0.55;
      if (r.shield > 0) {
        r.shield = 0;
        r.iFrames = 1.4;
        this._fun('STOMP BLOCK');
      } else if (r.boosting) {
        r.iFrames = 0.9;
        r.score += 150;
        this._fun('STOMP SURF');
      } else {
        this._applyHit(0.45, 'STOMP!');
      }
    }
  };

  CelestialRacers.prototype._applyHit = function (slow, label) {
    var r = this.race;
    r.speed *= 1 - slow;
    r.hitFlash = 0.35;
    r.shake = 0.4;
    r.iFrames = 1.35;
    r.pressure = Math.min(1, r.pressure + 0.12);
    r.score = Math.max(0, r.score - 80);
    this._fun(label || 'HIT');
    this.audio.hit();
  };

  CelestialRacers.prototype._spawnBurst = function (x, color) {
    var r = this.race;
    for (var i = 0; i < 8; i++) {
      r.particles.push({
        x: x + rand(-0.1, 0.1),
        y: 0,
        vx: rand(-0.8, 0.8),
        vy: rand(-1.5, -0.3),
        life: rand(0.3, 0.6),
        color: color
      });
    }
  };

  CelestialRacers.prototype._updateJuice = function (dt, boostActive) {
    var r = this.race;
    var i;
    for (i = r.particles.length - 1; i >= 0; i--) {
      var p = r.particles[i];
      p.life -= dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.life <= 0) r.particles.splice(i, 1);
    }
    if (boostActive && Math.random() < 0.5) {
      r.speedLines.push({ x: rand(-1, 1), life: 0.25, len: rand(0.3, 0.8) });
    }
    for (i = r.speedLines.length - 1; i >= 0; i--) {
      r.speedLines[i].life -= dt;
      if (r.speedLines[i].life <= 0) r.speedLines.splice(i, 1);
    }
  };

  CelestialRacers.prototype._fun = function (label) {
    var r = this.race;
    if (!r) return;
    r.funLabel = label;
    r.funTimer = 0.7;
    if (this.funEl) {
      this.funEl.textContent = label;
      this.funEl.classList.remove('cr-fun-pop');
      void this.funEl.offsetWidth;
      this.funEl.classList.add('cr-fun-pop');
      this.funEl.style.opacity = '1';
    }
  };

  CelestialRacers.prototype._updateHud = function () {
    var r = this.race;
    if (!r) return;
    var pct = percent(r.position);
    var el = function (id) { return document.getElementById(id); };
    var s = el('cr-score');
    if (s) s.textContent = 'SCORE ' + Math.floor(r.score);
    var d = el('cr-dist');
    if (d) d.textContent = Math.floor(pct) + '%';
    var sp = el('cr-speed');
    if (sp) sp.textContent = Math.floor(r.speed * 0.09) + ' km/h';
    var c = el('cr-coins');
    if (c) c.textContent = '★ ' + r.coins;
    var st = el('cr-status');
    if (st) {
      if (r.countdown > 0) st.textContent = r.countdown > 0.2 ? String(Math.ceil(r.countdown)) : 'GO';
      else st.textContent = phaseName(pct).toUpperCase();
    }
    var bf = el('cr-boost-fill');
    if (bf) bf.style.width = Math.floor(r.boostFuel * 100) + '%';
    var pf = el('cr-pressure-fill');
    if (pf) pf.style.width = Math.floor(r.pressure * 100) + '%';
    if (this.funEl && r.funTimer <= 0) this.funEl.style.opacity = '0';
  };

  CelestialRacers.prototype._finish = function (won) {
    var r = this.race;
    r.finished = true;
    r.finishFanfare = 1;
    this.audio.finishFanfare();
    var pct = percent(r.position);
    var t = r.time;
    var grade = 'C';
    if (won) {
      if (r.score >= 8000 && r.coins >= 12 && t < 75) grade = 'S';
      else if (r.score >= 5500 && r.coins >= 8) grade = 'A';
      else if (r.score >= 3000) grade = 'B';
      else grade = 'C';
    } else grade = 'D';

    var title = won ? 'FINISH!' : 'RUN OVER';
    var body =
      'Grade <strong>' + grade + '</strong><br>' +
      'Score ' + Math.floor(r.score) +
      ' · ★ ' + r.coins +
      ' · Passes ' + r.passes +
      ' · Time ' + t.toFixed(1) + 's<br>' +
      (won
        ? 'You raced past the Giant Reptilian stomps on the cosmic highway.'
        : 'The highway wins this time — Race Again.');
    document.getElementById('cr-result-title').innerHTML = title + ' <span class="cr-grade">' + grade + '</span>';
    document.getElementById('cr-result-body').innerHTML = body;
    this.show('results');
  };

  /* ========== Renderer ========== */
  CelestialRacers.prototype._draw = function (dt) {
    try {
      var ctx = this.ctx;
      var W = this.W;
      var H = this.H;
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, W, H);

      var shakeX = 0, shakeY = 0;
      if (this.race && this.race.shake > 0) {
        shakeX = (Math.random() - 0.5) * 10 * this.race.shake;
        shakeY = (Math.random() - 0.5) * 8 * this.race.shake;
      }
      ctx.save();
      ctx.translate(shakeX, shakeY);

      this._drawSky(ctx, W, H, dt);

      if (this.state === 'race' && this.race) {
        this._drawRoad(ctx, W, H);
        this._drawCar(ctx, W, H);
        this._drawJuice(ctx, W, H);
        this._drawReptilian(ctx, W, H);
        this._drawCountdown(ctx, W, H);
        if (this.race.hitFlash > 0) {
          ctx.fillStyle = 'rgba(255,40,100,' + (this.race.hitFlash * 0.45) + ')';
          ctx.fillRect(0, 0, W, H);
        }
        /* pressure vignette */
        if (this.race.pressure > 0.55) {
          var va = (this.race.pressure - 0.55) * 0.55;
          var g = ctx.createRadialGradient(W / 2, H / 2, H * 0.25, W / 2, H / 2, H * 0.75);
          g.addColorStop(0, 'rgba(0,0,0,0)');
          g.addColorStop(1, 'rgba(80,0,40,' + va + ')');
          ctx.fillStyle = g;
          ctx.fillRect(0, 0, W, H);
        }
      } else if (this.state === 'select') {
        this._drawIdleRoad(ctx, W, H, this.lastTs / 1000);
        this._drawCarPreview(ctx, W, H);
      } else {
        /* title / results backdrop */
        if (this._titleImg && this._titleImg.complete) {
          try {
            var img = this._titleImg;
            var scale = Math.max(W / img.width, H / img.height);
            var iw = img.width * scale;
            var ih = img.height * scale;
            ctx.globalAlpha = 0.45;
            ctx.drawImage(img, (W - iw) / 2, (H - ih) / 2, iw, ih);
            ctx.globalAlpha = 1;
          } catch (e) {}
        }
        this._drawIdleRoad(ctx, W, H, this.lastTs / 1000);
      }

      ctx.restore();
    } catch (drawErr) {
      try { console.error('CelestialRacers draw error', drawErr); } catch (e2) {}
    }
  };

  CelestialRacers.prototype._drawSky = function (ctx, W, H, dt) {
    var g = ctx.createLinearGradient(0, 0, 0, H * 0.72);
    g.addColorStop(0, '#09031a');
    g.addColorStop(0.45, '#1a0a3a');
    g.addColorStop(0.75, '#3a1460');
    g.addColorStop(1, '#120828');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    var off = (this.race && this.race.skyOffset) || 0;
    this._drawStars(ctx, W, H, dt, off);
    this._drawPlanet(ctx, W, H, off);
    this._drawCity(ctx, W, H, off);
  };

  CelestialRacers.prototype._drawStars = function (ctx, W, H, dt, off) {
    for (var i = 0; i < this.stars.length; i++) {
      var s = this.stars[i];
      s.tw += dt * 2;
      var a = 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(s.tw));
      var x = ((s.x + off * 0.15) % 1 + 1) % 1 * W;
      ctx.fillStyle = 'rgba(220,240,255,' + a + ')';
      ctx.fillRect(x, s.y * H, s.s, s.s);
    }
  };

  CelestialRacers.prototype._drawPlanet = function (ctx, W, H, off) {
    var px = W * 0.72 + Math.sin(off * 2) * 8;
    var py = H * 0.18;
    var pr = Math.min(W, H) * 0.09;
    var g = ctx.createRadialGradient(px - pr * 0.3, py - pr * 0.3, pr * 0.1, px, py, pr);
    g.addColorStop(0, '#ff9de0');
    g.addColorStop(0.45, '#c44ec8');
    g.addColorStop(1, 'rgba(40,10,60,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(px, py, pr, 0, Math.PI * 2);
    ctx.fill();
    /* ring */
    ctx.strokeStyle = 'rgba(94,231,255,0.35)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(px, py, pr * 1.55, pr * 0.35, -0.4, 0, Math.PI * 2);
    ctx.stroke();
  };

  CelestialRacers.prototype._drawCity = function (ctx, W, H, off) {
    var baseY = H * 0.52;
    /* distant mountains */
    ctx.fillStyle = '#0c0620';
    ctx.beginPath();
    ctx.moveTo(0, baseY + 20);
    for (var i = 0; i <= 12; i++) {
      var mx = (i / 12) * W + (off * 40) % 80;
      var mh = 30 + (Math.sin(i * 1.7 + off) * 0.5 + 0.5) * 50;
      ctx.lineTo(mx, baseY - mh);
    }
    ctx.lineTo(W, baseY + 20);
    ctx.closePath();
    ctx.fill();

    /* mid ridge pink rim */
    ctx.strokeStyle = 'rgba(255,78,205,0.35)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (i = 0; i <= 12; i++) {
      mx = (i / 12) * W + (off * 40) % 80;
      mh = 30 + (Math.sin(i * 1.7 + off) * 0.5 + 0.5) * 50;
      if (i === 0) ctx.moveTo(mx, baseY - mh);
      else ctx.lineTo(mx, baseY - mh);
    }
    ctx.stroke();

    /* city silhouette */
    var shift = (off * 120) % 60;
    for (i = -1; i < 20; i++) {
      var bx = i * 55 + shift;
      var bw = 28 + (i % 3) * 8;
      var bh = 25 + ((i * 17) % 55);
      ctx.fillStyle = i % 2 ? '#140a2c' : '#1a1038';
      ctx.fillRect(bx, baseY - bh, bw, bh + 30);
      if (i % 4 === 0) {
        ctx.fillStyle = 'rgba(94,231,255,0.25)';
        ctx.fillRect(bx + 4, baseY - bh + 6, 4, 4);
        ctx.fillStyle = 'rgba(255,78,205,0.3)';
        ctx.fillRect(bx + 12, baseY - bh + 14, 4, 4);
      }
    }
    /* horizon glow */
    var hg = ctx.createLinearGradient(0, baseY - 10, 0, baseY + 40);
    hg.addColorStop(0, 'rgba(255,78,205,0.15)');
    hg.addColorStop(1, 'rgba(5,2,12,0)');
    ctx.fillStyle = hg;
    ctx.fillRect(0, baseY - 10, W, 50);
  };

  CelestialRacers.prototype._drawIdleRoad = function (ctx, W, H, t) {
    var horizon = H * 0.55;
    for (var i = 0; i < 40; i++) {
      var p = i / 40;
      var y = horizon + p * p * (H - horizon);
      var w = 40 + p * W * 0.7;
      var dark = (Math.floor(i + t * 8) % 2) === 0;
      ctx.fillStyle = dark ? '#120c24' : '#1a1432';
      ctx.fillRect(W / 2 - w, y, w * 2, Math.max(2, (H - horizon) / 35));
      ctx.fillStyle = dark ? 'rgba(255,78,205,0.7)' : 'rgba(94,231,255,0.7)';
      ctx.fillRect(W / 2 - w - 6, y, 6, Math.max(2, (H - horizon) / 35));
      ctx.fillRect(W / 2 + w, y, 6, Math.max(2, (H - horizon) / 35));
    }
  };

  CelestialRacers.prototype._drawRoad = function (ctx, W, H) {
    var r = this.race;
    var segs = r.segments;
    var base = Math.floor(r.position / SEG_LEN);
    var camY = CAM_H + (r.camBob || 0);
    var camZ = r.position - PLAYER_Z;
    var playerX = r.playerXSmooth * ROAD_W;
    /* FOV punch via slight cam depth feel — scale road toward player */
    var fov = 1 + (r.fovPunch || 0) * 0.08;

    var x = 0;
    var dx = 0;
    var maxy = H;
    var visible = [];
    r._proj = true;

    for (var n = 0; n < DRAW_DIST; n++) {
      var idx = (base + n) % segs.length;
      var segment = segs[idx];
      segment._n = n;

      project(segment.p1, playerX - x, camY, camZ, CAM_DEPTH, W, H, ROAD_W * fov);
      project(segment.p2, playerX - x - dx, camY, camZ, CAM_DEPTH, W, H, ROAD_W * fov);

      x += dx;
      dx += segment.curve;

      var behind = segment.p1.camera.z <= CAM_DEPTH;
      var backface = segment.p2.screen.y >= segment.p1.screen.y;
      var clipped = segment.p2.screen.y >= maxy;
      if (behind || backface || clipped) continue;

      visible.push(segment);
      maxy = segment.p1.screen.y;
    }

    for (var k = visible.length - 1; k >= 0; k--) {
      this._renderSegment(ctx, W, H, visible[k]);
    }
    this._drawEntities(ctx, W, H);
    this._drawRivals(ctx, W, H);
  };

  CelestialRacers.prototype._renderSegment = function (ctx, W, H, segment) {
    var p1 = segment.p1.screen;
    var p2 = segment.p2.screen;
    if (!p1 || !p2) return;
    var y1 = p1.y;
    var y2 = p2.y;
    if (!(y1 > y2)) return;
    var dark = !!segment.color;
    var n = segment._n || 0;
    var fogT = Math.min(1, n / Math.max(1, DRAW_DIST * 0.92));

    var grass = dark ? '#050210' : '#140a2c';
    var shoulder = dark ? '#1a0a30' : '#2a1450';
    var rumble = dark ? '#ff2eb8' : '#3ef0ff';
    var rumbleAlt = dark ? '#ff6ad5' : '#9af7ff';
    var road = dark ? '#10101c' : '#1a1630';
    var roadStripe = dark ? '#18142c' : '#242040';
    var laneCol = dark ? 'rgba(200,230,255,0.25)' : 'rgba(255,255,255,0.82)';
    var railPink = 'rgba(255,78,205,' + (0.98 - fogT * 0.5) + ')';
    var railCyan = 'rgba(94,231,255,' + (0.98 - fogT * 0.5) + ')';
    var railGlow = 'rgba(255,120,220,' + (0.25 - fogT * 0.12) + ')';

    ctx.fillStyle = grass;
    ctx.fillRect(0, y2, W, Math.max(1, y1 - y2));

    var r1 = rumbleWidth(p1.w, LANES) * 1.75;
    var r2 = rumbleWidth(p2.w, LANES) * 1.75;
    var l1 = laneMarkerWidth(p1.w, LANES);
    var l2 = laneMarkerWidth(p2.w, LANES);
    var rw1 = railWidth(p1.w);
    var rw2 = railWidth(p2.w);
    var sh1 = r1 * 2.2;
    var sh2 = r2 * 2.2;

    poly(ctx, p1.x - p1.w - sh1, y1, p1.x - p1.w - r1, y1, p2.x - p2.w - r2, y2, p2.x - p2.w - sh2, y2, shoulder);
    poly(ctx, p1.x + p1.w + r1, y1, p1.x + p1.w + sh1, y1, p2.x + p2.w + sh2, y2, p2.x + p2.w + r2, y2, shoulder);
    poly(ctx, p1.x - p1.w - r1, y1, p1.x - p1.w, y1, p2.x - p2.w, y2, p2.x - p2.w - r2, y2, rumble);
    poly(ctx, p1.x + p1.w + r1, y1, p1.x + p1.w, y1, p2.x + p2.w, y2, p2.x + p2.w + r2, y2, rumbleAlt);
    poly(ctx, p1.x - p1.w, y1, p1.x + p1.w, y1, p2.x + p2.w, y2, p2.x - p2.w, y2, dark ? road : roadStripe);

    var cg = dark ? 'rgba(94,231,255,0.07)' : 'rgba(94,231,255,0.14)';
    poly(ctx, p1.x - p1.w * 0.22, y1, p1.x + p1.w * 0.22, y1, p2.x + p2.w * 0.22, y2, p2.x - p2.w * 0.22, y2, cg);

    if (!dark || n % 2 === 0) {
      var lanew1 = (p1.w * 2) / LANES;
      var lanew2 = (p2.w * 2) / LANES;
      var lanex1 = p1.x - p1.w + lanew1;
      var lanex2 = p2.x - p2.w + lanew2;
      for (var lane = 1; lane < LANES; lane++) {
        poly(ctx, lanex1 - l1 / 2, y1, lanex1 + l1 / 2, y1, lanex2 + l2 / 2, y2, lanex2 - l2 / 2, y2, laneCol);
        lanex1 += lanew1;
        lanex2 += lanew2;
      }
      poly(
        ctx,
        p1.x - l1 * 0.55, y1, p1.x + l1 * 0.55, y1,
        p2.x + l2 * 0.55, y2, p2.x - l2 * 0.55, y2,
        dark ? 'rgba(255,230,109,0.38)' : 'rgba(255,230,109,0.9)'
      );
    }

    /* Thick neon side rails — always readable */
    var glow1 = rw1 * 2.6;
    var glow2 = rw2 * 2.6;
    poly(ctx, p1.x - p1.w - r1 - glow1, y1, p1.x - p1.w - r1 + rw1 * 0.2, y1, p2.x - p2.w - r2 + rw2 * 0.2, y2, p2.x - p2.w - r2 - glow2, y2, railGlow);
    poly(ctx, p1.x + p1.w + r1 - rw1 * 0.2, y1, p1.x + p1.w + r1 + glow1, y1, p2.x + p2.w + r2 + glow2, y2, p2.x + p2.w + r2 - rw2 * 0.2, y2, railGlow);
    poly(ctx, p1.x - p1.w - r1 - rw1 * 1.2, y1, p1.x - p1.w - r1 + rw1 * 0.4, y1, p2.x - p2.w - r2 + rw2 * 0.4, y2, p2.x - p2.w - r2 - rw2 * 1.2, y2, railPink);
    poly(ctx, p1.x + p1.w + r1 - rw1 * 0.4, y1, p1.x + p1.w + r1 + rw1 * 1.2, y1, p2.x + p2.w + r2 + rw2 * 1.2, y2, p2.x + p2.w + r2 - rw2 * 0.4, y2, railCyan);

    if (fogT > 0.4) {
      var fa = (fogT - 0.4) / 0.6;
      ctx.fillStyle = 'rgba(28,10,55,' + (fa * 0.28) + ')';
      ctx.fillRect(0, y2, W, Math.max(1, y1 - y2));
    }
    if (fogT > 0.62) {
      var fa2 = (fogT - 0.62) / 0.38;
      ctx.fillStyle = 'rgba(255,78,205,' + (fa2 * 0.08) + ')';
      ctx.fillRect(0, y2, W, Math.max(1, y1 - y2));
      ctx.fillStyle = 'rgba(12,4,28,' + (fa2 * 0.42) + ')';
      ctx.fillRect(0, y2, W, Math.max(1, y1 - y2));
    }
  };

  CelestialRacers.prototype._projectSprite = function (z, x) {
    var r = this.race;
    var segs = r.segments;
    var base = Math.floor(r.position / SEG_LEN);
    var n = Math.floor((z - r.position) / SEG_LEN);
    if (n < 1 || n >= DRAW_DIST) return null;
    var idx = (base + n) % segs.length;
    var seg = segs[idx];
    if (!seg || !seg.p1.screen || !seg.p1.screen.scale) return null;
    var s = seg.p1.screen;
    var scale = s.scale;
    var sx = s.x + x * s.w * 1.65;
    var sy = s.y;
    var size = Math.max(4, 1800 * scale);
    return { x: sx, y: sy, size: size, scale: scale, n: n };
  };

  CelestialRacers.prototype._drawEntities = function (ctx, W, H) {
    var r = this.race;
    var list = [];
    for (var i = 0; i < r.entities.length; i++) {
      var e = r.entities[i];
      if (!e.alive) continue;
      var dz = e.z - r.position;
      if (dz < 40 || dz > DRAW_DIST * SEG_LEN * 0.9) continue;
      list.push(e);
    }
    list.sort(function (a, b) { return b.z - a.z; });
    for (i = 0; i < list.length; i++) {
      var p = this._projectSprite(list[i].z, list[i].x);
      if (!p) continue;
      this._drawEntity(ctx, list[i], p.x, p.y, p.size, p.scale);
    }
  };

  CelestialRacers.prototype._drawRivals = function (ctx, W, H) {
    var r = this.race;
    var list = r.rivals.slice().sort(function (a, b) { return b.z - a.z; });
    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      var dz = v.z - r.position;
      if (dz < 60 || dz > DRAW_DIST * SEG_LEN * 0.85) continue;
      var p = this._projectSprite(v.z, v.x);
      if (!p) continue;
      this._paintRival(ctx, p.x, p.y, p.size, v);
    }
  };

  CelestialRacers.prototype._paintRival = function (ctx, x, y, size, v) {
    var s = size * 0.55;
    var bob = Math.sin(v.bob) * s * 0.04;
    ctx.save();
    ctx.translate(x, y - s * 0.15 + bob);
    /* soft glow body */
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.beginPath();
    ctx.ellipse(0, s * 0.35, s * 0.35, s * 0.1, 0, 0, Math.PI * 2);
    ctx.fill();
    var g = ctx.createLinearGradient(0, -s * 0.4, 0, s * 0.3);
    g.addColorStop(0, '#fff');
    g.addColorStop(0.4, v.color);
    g.addColorStop(1, 'rgba(20,10,40,0.9)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(0, -s * 0.45);
    ctx.lineTo(s * 0.32, s * 0.1);
    ctx.lineTo(s * 0.18, s * 0.28);
    ctx.lineTo(-s * 0.18, s * 0.28);
    ctx.lineTo(-s * 0.32, s * 0.1);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = v.color;
    ctx.lineWidth = Math.max(1, s * 0.04);
    ctx.stroke();
    /* canopy */
    ctx.fillStyle = 'rgba(10,20,40,0.7)';
    ctx.beginPath();
    ctx.ellipse(0, -s * 0.12, s * 0.14, s * 0.1, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  CelestialRacers.prototype._drawEntity = function (ctx, e, x, y, size, scale) {
    var s = size;
    var pulse = 0.85 + 0.15 * Math.sin(e.pulse || 0);
    ctx.save();
    ctx.translate(x, y);

    /* pink telegraph for hazards */
    if (e.warn && e.warn > 0.05 && !e.scenery) {
      ctx.strokeStyle = 'rgba(255,78,205,' + (0.3 + e.warn * 0.6) + ')';
      ctx.lineWidth = Math.max(2, s * 0.04);
      ctx.beginPath();
      ctx.arc(0, -s * 0.1, s * (0.4 + e.warn * 0.3) * pulse, 0, Math.PI * 2);
      ctx.stroke();
    }

    if (e.kind === 'coin') {
      ctx.fillStyle = 'rgba(255,230,109,' + (0.35 * pulse) + ')';
      ctx.beginPath();
      ctx.arc(0, -s * 0.15, s * 0.38, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ffe66d';
      ctx.font = 'bold ' + Math.max(10, s * 0.55) + 'px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = '#ffe66d';
      ctx.shadowBlur = 12;
      ctx.fillText('★', 0, -s * 0.15);
      ctx.shadowBlur = 0;
    } else if (e.kind === 'boost') {
      ctx.fillStyle = 'rgba(94,231,255,0.3)';
      ctx.beginPath();
      ctx.arc(0, -s * 0.15, s * 0.32 * pulse, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#5ee7ff';
      ctx.font = 'bold ' + Math.max(11, s * 0.5) + 'px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('N', 0, -s * 0.15);
    } else if (e.kind === 'shield') {
      ctx.strokeStyle = '#a78bfa';
      ctx.lineWidth = Math.max(2, s * 0.06);
      ctx.beginPath();
      ctx.arc(0, -s * 0.15, s * 0.3 * pulse, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(167,139,250,0.45)';
      ctx.fill();
    } else if (e.kind === 'chrono') {
      ctx.fillStyle = '#7af0c8';
      ctx.beginPath();
      ctx.arc(0, -s * 0.15, s * 0.28, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = Math.max(1.5, s * 0.04);
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.15);
      ctx.lineTo(0, -s * 0.28);
      ctx.moveTo(0, -s * 0.15);
      ctx.lineTo(s * 0.12, -s * 0.1);
      ctx.stroke();
    } else if (e.kind === 'crack') {
      ctx.strokeStyle = 'rgba(255,78,205,' + (0.7 + 0.3 * pulse) + ')';
      ctx.lineWidth = Math.max(2, s * 0.05);
      ctx.beginPath();
      ctx.moveTo(-s * 0.35, -s * 0.05);
      ctx.lineTo(-s * 0.1, -s * 0.2);
      ctx.lineTo(s * 0.05, -s * 0.05);
      ctx.lineTo(s * 0.3, -s * 0.22);
      ctx.stroke();
    } else if (e.kind === 'debris') {
      ctx.fillStyle = '#6a5a80';
      ctx.beginPath();
      ctx.moveTo(-s * 0.2, -s * 0.05);
      ctx.lineTo(0, -s * 0.28);
      ctx.lineTo(s * 0.22, -s * 0.08);
      ctx.lineTo(s * 0.1, s * 0.05);
      ctx.lineTo(-s * 0.15, s * 0.02);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,78,205,0.5)';
      ctx.stroke();
    } else if (e.kind === 'stomp') {
      var warn = e.warn || 0;
      ctx.fillStyle = 'rgba(255,40,100,' + (0.25 + warn * 0.45) + ')';
      ctx.beginPath();
      ctx.ellipse(0, 0, s * (0.7 + warn * 0.4), s * 0.2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ff4ecd';
      ctx.lineWidth = Math.max(2, s * 0.06);
      ctx.stroke();
      ctx.fillStyle = '#ff9de0';
      ctx.font = 'bold ' + Math.max(10, s * 0.28) + 'px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('STOMP', 0, -s * 0.35);
    } else if (e.scenery) {
      this._drawScenery(ctx, e.kind, s, e.pulse || 0);
    }
    ctx.restore();
  };

  CelestialRacers.prototype._drawScenery = function (ctx, kind, s, pulse) {
    if (kind === 'pillar') {
      ctx.fillStyle = '#2a1850';
      ctx.fillRect(-s * 0.08, -s * 1.1, s * 0.16, s * 1.1);
      ctx.fillStyle = 'rgba(94,231,255,0.5)';
      ctx.fillRect(-s * 0.1, -s * 1.15, s * 0.2, s * 0.08);
    } else if (kind === 'crystal') {
      ctx.fillStyle = 'rgba(94,231,255,0.55)';
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.9);
      ctx.lineTo(s * 0.2, -s * 0.2);
      ctx.lineTo(0, 0);
      ctx.lineTo(-s * 0.2, -s * 0.2);
      ctx.closePath();
      ctx.fill();
    } else if (kind === 'arch') {
      ctx.strokeStyle = 'rgba(255,78,205,0.55)';
      ctx.lineWidth = Math.max(2, s * 0.06);
      ctx.beginPath();
      ctx.arc(0, -s * 0.1, s * 0.45, Math.PI, 0);
      ctx.stroke();
    } else if (kind === 'billboard') {
      ctx.fillStyle = '#1a1040';
      ctx.fillRect(-s * 0.35, -s * 0.85, s * 0.7, s * 0.45);
      ctx.strokeStyle = '#5ee7ff';
      ctx.strokeRect(-s * 0.35, -s * 0.85, s * 0.7, s * 0.45);
      ctx.fillStyle = '#ff4ecd';
      ctx.font = 'bold ' + Math.max(8, s * 0.18) + 'px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('IL', 0, -s * 0.58);
      ctx.fillStyle = '#2a1850';
      ctx.fillRect(-s * 0.04, -s * 0.4, s * 0.08, s * 0.4);
    } else if (kind === 'floatring') {
      ctx.strokeStyle = 'rgba(255,230,109,' + (0.4 + 0.3 * Math.sin(pulse)) + ')';
      ctx.lineWidth = Math.max(2, s * 0.05);
      ctx.beginPath();
      ctx.ellipse(0, -s * 0.5, s * 0.35, s * 0.12, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  CelestialRacers.prototype._drawCar = function (ctx, W, H) {
    var r = this.race;
    var car = r.car;
    var cx = W / 2 + r.playerXSmooth * W * 0.22;
    var cy = H * 0.78;
    var s = Math.min(W, H) * 0.11;
    var steer = this.touch.steerSmooth || 0;
    if (this.keys['arrowleft'] || this.keys['a']) steer = -1;
    if (this.keys['arrowright'] || this.keys['d']) steer = 1;
    this._paintCar(ctx, cx, cy, s, car, r.boosting, steer, r.shield > 0);
  };

  CelestialRacers.prototype._drawCarPreview = function (ctx, W, H) {
    var car = CARS[this.carIndex];
    this._paintCar(ctx, W / 2, H * 0.72, Math.min(W, H) * 0.12, car, false, Math.sin(this.lastTs / 700) * 0.3, false);
  };

  CelestialRacers.prototype._paintCar = function (ctx, cx, cy, s, car, boosting, steer, shielded) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(steer * 0.12);

    /* shadow */
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    ctx.beginPath();
    ctx.ellipse(0, s * 0.42, s * 0.55, s * 0.12, 0, 0, Math.PI * 2);
    ctx.fill();

    /* boost flame */
    if (boosting) {
      var flick = 0.7 + Math.random() * 0.3;
      var fg = ctx.createLinearGradient(0, s * 0.3, 0, s * 0.9 * flick);
      fg.addColorStop(0, '#fff');
      fg.addColorStop(0.3, '#5ee7ff');
      fg.addColorStop(0.7, '#ff4ecd');
      fg.addColorStop(1, 'rgba(255,100,50,0)');
      ctx.fillStyle = fg;
      ctx.beginPath();
      ctx.moveTo(-s * 0.18, s * 0.25);
      ctx.lineTo(0, s * (0.75 + Math.random() * 0.25));
      ctx.lineTo(s * 0.18, s * 0.25);
      ctx.closePath();
      ctx.fill();
    }

    var id = car.id;
    var body = car.body || car.accent;
    var g = ctx.createLinearGradient(0, -s * 0.5, 0, s * 0.4);
    g.addColorStop(0, '#fff');
    g.addColorStop(0.25, body);
    g.addColorStop(0.7, car.color);
    g.addColorStop(1, '#1a1030');

    ctx.fillStyle = g;
    ctx.strokeStyle = car.accent;
    ctx.lineWidth = Math.max(1.5, s * 0.04);

    if (id === 'nova') {
      /* sleek wedge */
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.55);
      ctx.lineTo(s * 0.38, s * 0.15);
      ctx.quadraticCurveTo(s * 0.42, s * 0.35, s * 0.2, s * 0.38);
      ctx.lineTo(-s * 0.2, s * 0.38);
      ctx.quadraticCurveTo(-s * 0.42, s * 0.35, -s * 0.38, s * 0.15);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    } else if (id === 'pulse') {
      /* rounded hover pod */
      ctx.beginPath();
      ctx.ellipse(0, 0, s * 0.4, s * 0.32, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-s * 0.25, s * 0.1);
      ctx.quadraticCurveTo(0, s * 0.45, s * 0.25, s * 0.1);
      ctx.stroke();
    } else if (id === 'comet') {
      /* sharp arrow */
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.6);
      ctx.lineTo(s * 0.35, s * 0.25);
      ctx.lineTo(s * 0.12, s * 0.4);
      ctx.lineTo(-s * 0.12, s * 0.4);
      ctx.lineTo(-s * 0.35, s * 0.25);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = car.accent;
      ctx.fillRect(-s * 0.04, -s * 0.2, s * 0.08, s * 0.45);
    } else {
      /* orbit — boxed guardian */
      ctx.beginPath();
      ctx.moveTo(-s * 0.3, -s * 0.35);
      ctx.lineTo(s * 0.3, -s * 0.35);
      ctx.lineTo(s * 0.4, s * 0.1);
      ctx.lineTo(s * 0.28, s * 0.38);
      ctx.lineTo(-s * 0.28, s * 0.38);
      ctx.lineTo(-s * 0.4, s * 0.1);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.strokeStyle = car.color;
      ctx.strokeRect(-s * 0.18, -s * 0.2, s * 0.36, s * 0.28);
    }

    /* canopy */
    ctx.fillStyle = 'rgba(10, 24, 48, 0.75)';
    ctx.beginPath();
    ctx.ellipse(0, -s * 0.08, s * 0.16, s * 0.12, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(94,231,255,0.5)';
    ctx.lineWidth = 1;
    ctx.stroke();

    /* headlights */
    ctx.fillStyle = '#fff';
    ctx.shadowColor = car.color;
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.arc(-s * 0.16, -s * 0.28, s * 0.05, 0, Math.PI * 2);
    ctx.arc(s * 0.16, -s * 0.28, s * 0.05, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    if (shielded) {
      ctx.strokeStyle = 'rgba(167,139,250,0.85)';
      ctx.lineWidth = Math.max(2, s * 0.05);
      ctx.beginPath();
      ctx.ellipse(0, 0, s * 0.55, s * 0.45, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();
  };

  CelestialRacers.prototype._drawJuice = function (ctx, W, H) {
    var r = this.race;
    var i;
    for (i = 0; i < r.speedLines.length; i++) {
      var sl = r.speedLines[i];
      var x = W / 2 + sl.x * W * 0.4;
      ctx.strokeStyle = 'rgba(94,231,255,' + (sl.life * 2) + ')';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, H * 0.3);
      ctx.lineTo(x + sl.x * 20, H * (0.3 + sl.len * 0.4));
      ctx.stroke();
    }
    /* particles in screen space approx */
    for (i = 0; i < r.particles.length; i++) {
      var p = r.particles[i];
      ctx.globalAlpha = clamp(p.life * 2, 0, 1);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(W / 2 + p.x * W * 0.25, H * 0.65 + p.y * 40, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  };

  CelestialRacers.prototype._drawCountdown = function (ctx, W, H) {
    var r = this.race;
    if (!r || r.countdown <= 0) return;
    var n = Math.ceil(r.countdown);
    var label = n >= 1 ? String(n) : 'GO';
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '900 ' + Math.floor(Math.min(W, H) * 0.22) + 'px sans-serif';
    ctx.fillStyle = n >= 1 ? '#5ee7ff' : '#ffe66d';
    ctx.shadowColor = ctx.fillStyle;
    ctx.shadowBlur = 30;
    ctx.fillText(label, W / 2, H * 0.4);
    ctx.restore();
  };

  CelestialRacers.prototype._drawReptilian = function (ctx, W, H) {
    var r = this.race;
    if (!r) return;
    var peak = Math.max(r.roarPulse, r.pressure > 0.7 ? (r.pressure - 0.7) / 0.3 : 0);
    if (peak <= 0.02) return;
    var a = clamp(peak, 0, 1);
    ctx.save();
    ctx.globalAlpha = a * 0.85;
    var cx = W / 2;
    var cy = H * 0.28;
    var s = Math.min(W, H) * 0.22 * (0.85 + 0.15 * Math.sin((r.time || 0) * 6));

    /* readable Giant Reptilian silhouette */
    ctx.fillStyle = '#0a0414';
    ctx.beginPath();
    /* head */
    ctx.moveTo(cx - s * 0.35, cy);
    ctx.quadraticCurveTo(cx - s * 0.5, cy - s * 0.55, cx, cy - s * 0.7);
    ctx.quadraticCurveTo(cx + s * 0.5, cy - s * 0.55, cx + s * 0.35, cy);
    /* jaws */
    ctx.lineTo(cx + s * 0.45, cy + s * 0.15);
    ctx.lineTo(cx + s * 0.15, cy + s * 0.05);
    ctx.lineTo(cx, cy + s * 0.25);
    ctx.lineTo(cx - s * 0.15, cy + s * 0.05);
    ctx.lineTo(cx - s * 0.45, cy + s * 0.15);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = 'rgba(255,78,205,' + (0.5 + a * 0.5) + ')';
    ctx.lineWidth = 2;
    ctx.stroke();

    /* eyes */
    ctx.fillStyle = '#ff4ecd';
    ctx.shadowColor = '#ff4ecd';
    ctx.shadowBlur = 16;
    ctx.beginPath();
    ctx.ellipse(cx - s * 0.14, cy - s * 0.28, s * 0.06, s * 0.04, -0.3, 0, Math.PI * 2);
    ctx.ellipse(cx + s * 0.14, cy - s * 0.28, s * 0.06, s * 0.04, 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    /* spines */
    ctx.strokeStyle = 'rgba(94,231,255,0.45)';
    for (var i = -2; i <= 2; i++) {
      ctx.beginPath();
      ctx.moveTo(cx + i * s * 0.08, cy - s * 0.55);
      ctx.lineTo(cx + i * s * 0.08, cy - s * (0.75 + Math.abs(i) * 0.05));
      ctx.stroke();
    }

    ctx.fillStyle = 'rgba(255,157,224,' + (0.4 + a * 0.4) + ')';
    ctx.font = 'bold ' + Math.max(11, s * 0.14) + 'px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Giant Reptilian', cx, cy + s * 0.45);
    ctx.restore();
  };

  /* ========== Mount API ========== */
  CelestialRacers.ENGINE = 'CR_ENGINE_V3';
  CelestialRacers.VERSION = 3;

  CelestialRacers.mount = function (selector, opts) {
    opts = opts || {};
    opts.root = selector;
    return new CelestialRacers(opts);
  };

  global.CelestialRacers = CelestialRacers;
})(typeof window !== 'undefined' ? window : this);
